/* eslint-disable */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { eventsApi } from "./api";
import { FormDatePicker, FormTimePicker } from "./dateTimePickers";
import { LocationSection, locFromEvent, locationPayload, validateLocation } from "./location";
import "./EventForm.css";

export default function EventForm() {
  const { id } = useParams();        // undefined → create mode
  const isEdit = !!id;
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [loc, setLoc] = useState(() => locFromEvent(null));
  const [category, setCategory] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState("");

  const patchLoc = (patch) => setLoc((l) => ({ ...l, ...patch }));

  useEffect(() => {
    if (!isEdit) return;
    eventsApi.get(id).then((ev) => {
      setTitle(ev.title || "");
      setDescription(ev.description || "");
      setEventDate(ev.event_date || "");
      setStartTime(ev.start_time || "");
      setEndTime(ev.end_time || "");
      setLoc(locFromEvent(ev));
      setCategory(ev.category || "");
      setLoading(false);
    }).catch((e) => {
      setError(e.message || "Could not load event");
      setLoading(false);
    });
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError("Title is required"); return; }
    if (!eventDate) { setError("Event date is required"); return; }
    if (startTime && endTime && endTime <= startTime) {
      setError("End time must be after start time");
      return;
    }
    const locError = validateLocation(loc);
    if (locError) { setError(locError); return; }
    setSaving(true);
    setError("");
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || undefined,
        event_date: eventDate,
        start_time: startTime || undefined,
        end_time: endTime || undefined,
        category: category.trim() || undefined,
        ...locationPayload(loc),
      };
      if (isEdit) {
        await eventsApi.patch(id, body);
        navigate(`/events/${id}`);
      } else {
        const ev = await eventsApi.create(body);
        navigate(`/events/${ev.id}`);
      }
    } catch (e) {
      setError(e.message || "Could not save event");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="evf-loading">Loading…</div>;

  return (
    <div className="evf-page">
      <div className="evf-back-bar">
        <button className="evf-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} /> Back
        </button>
      </div>
      <h1 className="evf-heading">{isEdit ? "Edit Event" : "New Event"}</h1>
      {error && <p className="evf-error">{error}</p>}
      <form className="evf-form" onSubmit={handleSubmit}>
        <label className="evf-label">
          Title <span className="evf-req">*</span>
          <input
            className="evf-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Vinayaka Chavithi Celebrations"
          />
        </label>
        <div className="evf-label">
          <span>Event Date <span className="evf-req">*</span></span>
          <FormDatePicker className="evf-input" value={eventDate} onChange={setEventDate} />
        </div>
        <div className="evf-row">
          <div className="evf-label evf-half">
            <span>Start Time</span>
            <FormTimePicker className="evf-input" value={startTime} onChange={setStartTime} />
          </div>
          <div className="evf-label evf-half">
            <span>End Time</span>
            <FormTimePicker className="evf-input" value={endTime} onChange={setEndTime} />
          </div>
        </div>
        <div className="evf-label">
          <span>Location</span>
          <LocationSection loc={loc} onChange={patchLoc} inputClass="evf-input" />
        </div>
        <label className="evf-label">
          Category
          <input
            className="evf-input"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="e.g. Festival, Yoga, Meeting"
          />
        </label>
        <label className="evf-label">
          Description
          <textarea
            className="evf-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What's happening? Any details residents should know?"
            rows={4}
          />
        </label>
        <button className="evf-submit" type="submit" disabled={saving}>
          <Save size={16} />
          {saving ? "Saving…" : isEdit ? "Save changes" : "Create event & notify residents"}
        </button>
      </form>
    </div>
  );
}
